import React, { useRef } from 'react';
import { IconUpload } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { Trans } from '@lingui/react/macro';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { TDocumentType } from 'lendingApi/lapiOverrides.types';
import { fetchSaveUploadedDocuments } from 'lendingApi/lendingApi';
import { useGetIntroducerApplication } from 'lendingApi/lendingApiHooks';
import { IwQueryClient } from 'store/IwQueryClient';
import { TUploadRejectReasonCode } from 'types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { translateUploadError } from 'utils/uploadErrors';
import { v4 } from 'uuid';

type TProps = {
  isOptional?: boolean;
  documentType?: TDocumentType;
  setFileLoading: (id: string, name: string) => void;
  setFileSuccess: (id: string) => void;
  setFileError: (id: string, errorReason: string) => void;
  setFileReject: (id: string, rejectReason: TUploadRejectReasonCode) => void;
};

export const UploadButton = ({
  isOptional,
  documentType = 'other',
  setFileLoading,
  setFileSuccess,
  setFileError,
  setFileReject,
}: TProps) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { trackEvent } = useAmplitude();
  const { application } = useGetIntroducerApplication();
  const { checkIntroducerPermission } = useIntroducerData();
  const stateKey = application?.customer?.state_key;
  const isPartnerChannel = application?.is_partner_channel || false;

  const onButtonClick = () => {
    trackEvent(AMPLITUDE_EVENTS.documentUploadClicked, {
      'Document type': documentType,
    });
    inputFileRef?.current?.click();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !stateKey) {
      return;
    }

    const files = Array.from(e.target.files);

    await Promise.all(
      files.map(async (file) => {
        const uuid = v4();
        setFileLoading(uuid, file.name);
        const body = new FormData();

        body.append('document', file);
        body.append('document_type', documentType);

        const response = await fetchSaveUploadedDocuments({
          stateKey,
          body,
        });

        IwQueryClient.getOrCreate().invalidateQueries({
          queryKey: ['documents', stateKey],
        });

        if (response?.errors?.[0]) {
          setFileError(uuid, translateUploadError(response.errors[0]));
        } else if (response?.attributes?.status === 'rejected') {
          setFileReject(uuid, response?.attributes?.reject_reason);
        } else {
          setFileSuccess(uuid);
        }
      }),
    );

    // Reset input value to allow same file upload to fire onChange.
    e.target.value = '';
  };

  return (
    <>
      <Button
        variant={isOptional ? 'tertiary' : 'secondary'}
        onClick={onButtonClick}
        disabled={
          !isPartnerChannel
            ? !checkIntroducerPermission('can_upload_broker_documents')
            : !checkIntroducerPermission('can_upload_partner_documents')
        }
      >
        <Trans>Upload</Trans> <IconUpload />
      </Button>
      <input
        multiple
        hidden
        data-testid="upload-input"
        name={documentType.replace(/\s+/g, '_')}
        type="file"
        ref={inputFileRef}
        accept=".jpg, .jpeg, .png, .pdf"
        onChange={handleChange}
      />
    </>
  );
};
